"use client";

import {
  type HeatmapCellSelectDetail,
  HeatmapCells,
  HeatmapChart,
  HeatmapInteractionBoundary,
  HeatmapInteractionProvider,
  HeatmapLegend,
  HeatmapTooltip,
  HeatmapXAxis,
  HeatmapYAxis,
  heatmapProjectionFits,
} from "@/components/charts/heatmap";
import {
  type ChartView,
  ChartViewToggle,
  chartViewToMode,
} from "@/components/charts/projection/chart-view-toggle";
import { ChartAxisFrame } from "@/components/charts/chart-axis-frame";
import { SwipeScroll } from "@/components/charts/swipe-scroll";
import { emitModuleInteraction } from "@/lib/module-interaction";
import { cn } from "@/lib/utils";
import { ModuleChartHeatmapData } from "@/types/modules";
import { chartGrayscaleStyle } from "../imports-token-scope";
import { JSX, useMemo, useState } from "react";
import { CHART_PLOT_HEIGHT, MIN_PX_PER_POINT } from "@/config/layout";
import { toHeatmapGrid } from "./heatmap-grid";

const DEFAULT_DATA: ModuleChartHeatmapData = {
  points: (() => {
    const pts: { x: number; y: number; value: number }[] = [];
    for (let week = 0; week < 12; week++) {
      for (let day = 0; day < 7; day++) {
        pts.push({ x: week, y: day, value: (week * 7 + day) % 7 });
      }
    }
    return pts;
  })(),
};

const CHART_HEIGHT = CHART_PLOT_HEIGHT.heatmap;
const CHART_MARGIN = { top: 28, right: 8, bottom: 0, left: 0 };

/**
 * Narrowest a cell may get before the grid stops shrinking and scrolls
 * sideways instead. Below this a 10-week grid stops reading as a grid.
 */
const MIN_CELL_SIZE = MIN_PX_PER_POINT.heatmap;

function emitCellSelect({
  column,
  row,
  index,
  selected,
  element,
}: HeatmapCellSelectDetail): void {
  emitModuleInteraction(element, selected ? "select" : "deselect", {
    kind: "cell",
    id: `${column}:${row}`,
    index,
  });
}

export default function ModuleHeatmapChart({
  view3d,
  data = DEFAULT_DATA,
  grayscale,
  className,
}: {
  view3d?: boolean;
  data?: ModuleChartHeatmapData;
  grayscale?: boolean;
  className?: string;
}): JSX.Element {
  const grid = useMemo(() => toHeatmapGrid(data.points), [data.points]);
  const { columnLabels, rowLabels } = grid;
  const canProject =
    Boolean(view3d) &&
    heatmapProjectionFits(
      grid.columns.length,
      grid.columns[0]?.bins.length ?? 0
    );
  const [view, setView] = useState<ChartView>("3d");
  const projection = canProject ? chartViewToMode(view) : undefined;
  const formatTitle = useMemo(() => {
    if (!(columnLabels && rowLabels)) {
      return undefined;
    }
    return ({ column, row }: { column: number; row: number }) =>
      `${columnLabels[column] ?? ""} · ${rowLabels[row] ?? ""}`;
  }, [columnLabels, rowLabels]);

  return (
    <div
      className={cn("flex w-full flex-col gap-3", className)}
      style={chartGrayscaleStyle(grayscale)}
    >
      {canProject ? (
        <div className="flex h-8 items-center justify-end">
          <ChartViewToggle
            className="shrink-0"
            kind="heatmap"
            onChange={setView}
            value={view}
          />
        </div>
      ) : null}
      <HeatmapInteractionProvider>
        <HeatmapInteractionBoundary className="flex flex-col gap-3">
          <ChartAxisFrame>
            <SwipeScroll className="min-w-0 flex-1" fade="auto">
              <div style={{ height: CHART_HEIGHT }}>
                <HeatmapChart
                  data={grid.columns}
                  layout="fill"
                  margin={CHART_MARGIN}
                  minBinSize={MIN_CELL_SIZE}
                  projection={projection}
                >
                  <HeatmapCells
                    hideGhostCells={false}
                    onCellSelect={emitCellSelect}
                  />
                  <HeatmapXAxis labels={columnLabels ?? undefined} />
                  <HeatmapYAxis
                    labels={rowLabels ?? undefined}
                    tickFilter={rowLabels ? "all" : "odd"}
                  />
                  <HeatmapTooltip
                    formatLabel={
                      formatTitle ? (count) => String(count) : undefined
                    }
                    formatSubtitle={formatTitle ? () => null : undefined}
                    formatTitle={formatTitle}
                  />
                </HeatmapChart>
              </div>
            </SwipeScroll>
          </ChartAxisFrame>
          <HeatmapLegend />
        </HeatmapInteractionBoundary>
      </HeatmapInteractionProvider>
    </div>
  );
}
